<template>
  <panel-layout
    id="customField-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div v-if="customField" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ customField.isNewRecord ? $t('customPage.new', { description: $t(CustomFieldModelTypeLabels[customField.modelType as CustomFieldModelType]) }) : customField.name }}
          </h2>
          <div v-if="!customField.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200" v-html="$t('pages.customField.edit.id', { id: customField.idCustomField })" />
          </div>
        </div>
      </div>
    </template>

    <o-form v-if="customField" ref="form" class="h-full w-full">
      <o-scroll class="h-full w-full">
        <div class="w-full px-6 py-4">
        <div class="flex space-x-4 mb-6">
          <o-field-group
            v-slot="{ status }"
            rules="required"
            class="w-full"
            :label="$t('general.name')"
            :name="$t('general.name')"
            :info="$t('pages.customField.edit.name')"
          >
            <text-field v-model="customField.name" :label="$t('general.name')" :status="status" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group
            v-slot="{ status }"
            rules="required"
            class="w-full"
            :label="$t('general.code')"
            :name="$t('general.code')"
            :info="$t('pages.customField.edit.code')"
          >
            <text-field v-model="customField.code" :label="$t('general.code')" :status="status" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group
            v-slot="{ status }"
            rules="required"
            class="w-full"
            :label="$t('general.dataType')"
            :name="$t('general.dataType')"
            :info="$t('pages.customField.edit.type')"
          >
            <select-field v-model="customField.dataType" :options="dataTypeOptions" :label="$t('general.dataType')" :status="status" @change="handleDataTypeChange" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group
            col
            reverse
            class="w-full md:w-1/2"
            :label="$t('general.history')"
            :name="$t('general.history')"
            :info="$t('pages.customField.edit.history')"
          >
            <toggle-field v-model="customField.history" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group col reverse class="w-full md:w-1/2" :label="$t('general.customerVisible')" :info="$t('pages.customField.edit.customerVisible')">
            <toggle-field v-model="customField.customerVisible" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group col reverse class="w-full md:w-1/2" :label="$t('pages.customField.edit.customerEditable')" :info="$t('pages.customField.edit.customerEditableInfo')">
            <toggle-field v-model="customField.customerEditable" />
          </o-field-group>
        </div>

        <div v-if="isDataTypeMultiple" class="flex space-x-4 mb-6">
          <o-field-group
            v-slot="{ status }"
            rules="required"
            class="w-full"
            :label="$t('general.values')"
            :name="$t('general.values')"
            :info="$t('pages.customField.edit.values')"
          >
            <list-field v-model="customField.multipleValues" :label="$t('general.values')" :status="status" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-6">
          <o-field-group
            v-slot="{ status }"
            class="w-full"
            :label="$t('general.defaultValue')"
            :name="$t('general.defaultValue')"
            :info="$t('pages.customField.edit.defaultValue')"
          >
            <component
              :is="defaultValueComponent"
              v-model="customField.defaultValue"
              :options="defaultValueOptions"
              :label="$t('general.defaultValue')"
              :status="status"
              :augmenter="Augmenters.timestamp"
            />
          </o-field-group>
        </div>
        </div>
      </o-scroll>
    </o-form>

    <template #footer>
      <div v-if="customField">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button variant="primary" outline icon="save" class="mr-2" @click="save()">
              {{ $t('general.save') }}
            </o-button>
            <o-button variant="primary" icon="save" class="mr-2" @click="save(true)">
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import {
  TextField,
  NumberField,
  ToggleField,
  SelectField,
  ListField,
  DateField,
  CheckboxField,
  CheckboxGroupField,
  TextAreaField,
} from '@/components/fields'

import { CustomFieldDataType, CustomFieldDataTypeLabels, CustomFieldModelType, CustomFieldModelTypeLabels } from '~/_api/models/customField'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{
  id?: string
  model?: string
}>()

const { $api, $notification, $bus, $i18n } = useNuxtApp()

const customField = ref<any>(null)
const loading = ref(true)

const confirmDialog = ref<any>()
const form = ref<any>()

const { unsavedChanges, snapshot } = useUnsavedChanges(customField)

const dataTypeOptions = computed(() => {
  return Object.values(CustomFieldDataType)
    .filter((v): v is CustomFieldDataType => typeof v === 'number')
    .map((value) => {
      return {
        text: $i18n.t(CustomFieldDataTypeLabels[value]),
        value,
      }
    })
})

const defaultValueComponent = computed(() => {
  switch (customField.value.dataType) {
    case CustomFieldDataType.longtext:
      return TextAreaField
    case CustomFieldDataType.multiple:
      return SelectField
    case CustomFieldDataType.bool:
      return SelectField
    case CustomFieldDataType.number:
      return NumberField
    case CustomFieldDataType.date:
      return DateField
    default:
      return TextField
  }
})

const defaultValueOptions = computed(() => {
  const noneOption = {
    text: $i18n.t('general.none'),
    value: null,
  }

  switch (customField.value.dataType) {
    case CustomFieldDataType.multiple:
      return [
        noneOption,
        ...customField.value.multipleValues.map((value: any) => {
          return { text: value, value }
        }),
      ]
    case CustomFieldDataType.bool:
      return [
        noneOption,
        { text: $i18n.t('general.yes'), value: true },
        { text: $i18n.t('general.no'), value: false },
      ]
    default:
      return undefined
  }
})

const isDataTypeText = computed(() => customField.value.isDataTypeText)
const isDataTypeLongText = computed(() => customField.value.isDataTypeLongText)
const isDataTypeMultiple = computed(() => customField.value.isDataTypeMultiple)
const isDataTypeBool = computed(() => customField.value.isDataTypeBool)
const isDataTypeNumber = computed(() => customField.value.isDataTypeNumber)
const isDataTypeDate = computed(() => customField.value.isDataTypeDate)

const save = async (close = false) => {
  if (!await form.value?.validate()) return false

  loading.value = true

  try {
    const result = (await $api.customField.save(customField.value)).data

    if (result) {
      customField.value = result
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.customField.edit.success'),
        text: $i18n.t('pages.customField.edit.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('CustomField', result)

      if (close) {
        panel.close()
      }
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
    loading.value = false
    return false
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const handleDataTypeChange = () => {
  customField.value.defaultValue = null
}

async function init(id?: string) {
  loading.value = true
  try {
    if (!id) {
      customField.value = (await $api.customField.defaults({} as any)).data

      if (props.model) {
        const type = Number(props.model)
        customField.value.modelType = type as CustomFieldModelType
      }
    } else {
      customField.value = (await $api.customField.get(id)).data

      if (!isDataTypeMultiple.value) {
        customField.value.multipleValues = []
      }
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }
  loading.value = false
}

watch(() => props.id, (id) => init(id), { immediate: true })
</script>
